import {
  apiScopeHint,
  apiScopeLabel,
  type PartnerConsentQuery,
  type PartnerTarget,
} from '@kanos/domain';
import { Button, CheckboxGroupField } from '@kanos/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ExternalLink, FlaskConical, PlugZap, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import {
  partnerConsentQuery,
  useInstallPartnerApp,
  type PartnerConsent,
} from '../firm/partner-queries';
import { firmProfileQuery } from '../firm/queries';
import { useMe } from '../me';
import { FormAlert, ProblemAlert, QueryContent } from '@kanos/web-core';

/** What a partner app sends along (ADR-015 §2) - checked by the API, not here. */
const Search = z.object({
  partner: z.string().catch(''),
  scope: z.string().catch(''),
  redirect_uri: z.string().catch(''),
  state: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/partner/connect')({
  validateSearch: Search,
  component: ConnectPartnerApp,
});

/**
 * Where a partner app sends a firm's administrator: connect the app to the active firm or its
 * sandbox with the scopes it asks for, or decline. An app still in testing connects to the sandbox
 * only. Either way the browser returns to the app's registered address.
 */
function ConnectPartnerApp() {
  const me = useMe();
  const search = Route.useSearch();
  const query: PartnerConsentQuery = {
    redirectUri: search.redirect_uri,
    scope: search.scope,
    ...(search.state === undefined ? {} : { state: search.state }),
  };
  const consent = useQuery({
    ...partnerConsentQuery(me.activeTenantId, search.partner, query),
    enabled: me.firmAdmin && /^[0-9a-f-]{36}$/i.test(search.partner),
  });

  return (
    <div className="mx-auto grid w-full max-w-xl gap-6">
      <header className="grid gap-2">
        <h1 className="text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">App verbinden</h1>
        <p className="text-muted">
          Eine App eines Partners möchte auf Daten Ihrer Kanzlei zugreifen.
        </p>
      </header>
      <section className="glass grid gap-5 rounded-lg p-5 sm:p-6" aria-label="Zustimmung">
        {!me.firmAdmin ? (
          <p className="text-sm text-muted">
            Apps verbinden nur Admins der Kanzlei. Bitte wenden Sie sich an sie.
          </p>
        ) : consent.isPending && !consent.isFetching ? (
          <FormAlert>
            Der Link der App ist unvollständig. Bitte starten Sie in der App neu.
          </FormAlert>
        ) : (
          <QueryContent query={consent}>
            {(data) => <Decision consent={data} query={query} partnerAppId={search.partner} />}
          </QueryContent>
        )}
      </section>
    </div>
  );
}

function Decision({
  consent,
  query,
  partnerAppId,
}: {
  consent: PartnerConsent;
  query: PartnerConsentQuery;
  partnerAppId: string;
}) {
  const me = useMe();
  const firm = useQuery(firmProfileQuery(me.activeTenantId));
  const install = useInstallPartnerApp(me.activeTenantId);
  const { partnerApp } = consent;
  // Into the firm only with the agreement confirmed (decided 2026-10-31, #40).
  const [dpa, setDpa] = useState<'dpa'[]>([]);
  const confirmed = dpa.length > 0;

  const { sandbox } = consent;
  const firmName = firm.data?.name ?? 'Ihrer Kanzlei';
  const connect = (target: PartnerTarget) =>
    install.mutate(
      {
        partnerAppId,
        scopes: consent.scopes,
        redirectUri: query.redirectUri,
        target,
        ...(target === 'firm' ? { dpaConfirmed: confirmed } : {}),
        ...(query.state === undefined ? {} : { state: query.state }),
      },
      { onSuccess: ({ redirectTo }) => window.location.assign(redirectTo) },
    );
  const pending = (target: PartnerTarget) =>
    install.isPending && (install.variables.target ?? 'firm') === target;
  const toFirm = partnerApp.released && !consent.installed;
  const toSandbox = sandbox !== null && !sandbox.installed;

  return (
    <div className="grid gap-5">
      <div className="flex items-start gap-3">
        <PlugZap aria-hidden className="mt-1 size-5 shrink-0 text-muted" />
        <div className="grid gap-1">
          <h2 className="text-xl font-semibold tracking-[-0.02em]">{partnerApp.name} verbinden?</h2>
          <p className="text-sm text-muted">
            von {partnerApp.vendor} ·{' '}
            <a
              href={partnerApp.website}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 underline underline-offset-2"
            >
              Website des Anbieters
              <ExternalLink aria-hidden className="size-3.5" />
            </a>
          </p>
        </div>
      </div>

      <div className="grid gap-2">
        <h3 className="text-sm font-semibold">Die App erhält</h3>
        <ul className="grid gap-2">
          {consent.scopes.map((scope) => (
            <li key={scope} className="grid gap-0.5 rounded-sm bg-fill px-4 py-3 text-sm">
              <span className="font-semibold">{apiScopeLabel[scope]}</span>
              <span className="text-muted">{apiScopeHint[scope]}</span>
            </li>
          ))}
        </ul>
      </div>

      {partnerApp.released ? (
        <p className="flex gap-3 text-sm text-muted">
          <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span>
            Kanos hat den Anbieter und die App geprüft. Die App erreicht nur, womit Sie sie
            verbinden, und nur im genannten Umfang. Sie trennen sie jederzeit unter Kanzlei →
            Verbundene Apps.
          </span>
        </p>
      ) : (
        <p className="flex gap-3 rounded-sm bg-warning-bg px-4 py-3 text-sm text-warning-fg">
          <FlaskConical aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span>
            Diese App ist noch in der Testphase: Kanos hat sie noch nicht freigegeben. Sie lässt
            sich nur mit der Sandbox Ihrer Kanzlei verbinden – dort gibt es nur erfundene Daten.
            {sandbox === null &&
              ' Eröffnen Sie dafür zuerst die Sandbox unter Kanzlei → Sandbox für Dienstleister.'}
          </span>
        </p>
      )}

      {consent.installed && (
        <FormAlert>
          Diese App ist mit der Kanzlei schon verbunden. Für andere Berechtigungen trennen Sie sie
          zuerst unter Kanzlei → Verbundene Apps.
        </FormAlert>
      )}
      {sandbox?.installed && (
        <FormAlert>
          Diese App ist mit der Sandbox schon verbunden. Für andere Berechtigungen trennen Sie sie
          zuerst unter Kanzlei → Sandbox für Dienstleister.
        </FormAlert>
      )}
      {toFirm && (
        <div className="grid gap-2">
          <CheckboxGroupField
            label="Auftragsverarbeitung"
            options={[
              {
                id: 'dpa',
                label: `Wir haben mit ${partnerApp.vendor} einen Vertrag zur Auftragsverarbeitung abgeschlossen oder schließen ihn ab.`,
                description:
                  'Er verpflichtet den Anbieter zur Verschwiegenheit nach § 62a StBerG (Art. 28 DSGVO). Kanos hält Ihre Bestätigung mit Person und Zeitpunkt fest.',
              },
            ]}
            value={dpa}
            onChange={setDpa}
            isRequired
            {...(toSandbox
              ? {
                  description:
                    'Für die Sandbox ist keine Bestätigung nötig: dort gibt es nur erfundene Daten.',
                }
              : {})}
          />
          {partnerApp.dpaUrl && (
            <a
              href={partnerApp.dpaUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex min-h-11 items-center gap-1 justify-self-start text-sm underline underline-offset-2"
            >
              Vertrag zur Auftragsverarbeitung von {partnerApp.vendor} lesen
              <ExternalLink aria-hidden className="size-3.5" />
            </a>
          )}
        </div>
      )}
      {install.error ? <ProblemAlert error={install.error} /> : null}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="quiet" onPress={() => window.location.assign(consent.declineTo)}>
          Ablehnen
        </Button>
        {toSandbox && (
          <Button
            writes
            variant={toFirm ? 'secondary' : 'primary'}
            isPending={pending('sandbox')}
            onPress={() => connect('sandbox')}
          >
            Mit Sandbox verbinden
          </Button>
        )}
        {toFirm && (
          <Button
            writes
            isPending={pending('firm')}
            isDisabled={!confirmed}
            onPress={() => connect('firm')}
          >
            Mit {firmName} verbinden
          </Button>
        )}
      </div>
    </div>
  );
}
